import React, { useState, useEffect, useCallback, useMemo, useReducer, useRef } from 'react';
import { useScrollLock } from '../../_utils/useScrollLock';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { newServicesAction } from '../../_actions';
import { productAction } from '../../_actions/product.action';
import LoyaltyCard from './LoyaltyCard';
import { RewardUnlockedView } from '../wallet/StampEarnedCelebration';
import '../wallet/StampEarnedCelebration.css';
import DesignerTopContextBar from '../gift-cards-v2/DesignerTopContextBar';
import DesignerContextMenu from '../gift-cards-v2/DesignerContextMenu';
import AbstractScene from '../gift-cards-v2/AbstractScene';
import ShapeRenderer from '../gift-cards-v2/ShapeRenderer';
import ColourPicker from '../shared/ColourPicker';
import NumberField from '../common/NumberField';
import toast from '../shared/Toast';
import {
  FONT_OPTIONS,
  SHAPE_TYPES,
  TEXT_PRESETS,
  OCCASIONS,
  TEXT_DEFAULTS,
  mintElementId,
} from '../gift-cards-v2/data';
import {
  THEMES,
  CARD_TEMPLATES,
  ICON_PACKS,
  STAMP_LAYOUTS,
  FILL_STYLES,
  ORIENTATIONS,
  STAMP_SHAPES,
  HEADER_STYLES,
  SLOT_LABELS,
  STAMP_BLOCKS,
  packForIcon,
  blankCustom,
  materialiseTemplate,
  headerToElements,
} from './data';
import '../gift-cards-v2/GiftCardsV2.css';
import '../shared/SlidePanel.css';
import '../shared/ui.css';

// LoyaltyDesigner — the card designer in the Beu editor chrome, now with
// the full gift-card creative toolkit (backgrounds, shapes, images, text
// decorations) layered around the structured stamp card, plus the scheme
// rules living inside the editor rather than on the page.

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <path d="M3 3l8 8M11 3l-8 8" />
  </svg>
);
const UndoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 4L2.5 7.5 6 11" /><path d="M2.5 7.5H10a3.5 3.5 0 0 1 0 7H8.5" />
  </svg>
);
const RedoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 4l3.5 3.5L10 11" /><path d="M13.5 7.5H6a3.5 3.5 0 0 0 0 7h1.5" />
  </svg>
);
const BackChevron = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 3.5L5.5 8l4.5 4.5" />
  </svg>
);

const IconRules = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 6h16M4 12h16M4 18h9" /><circle cx="18.5" cy="18" r="2.6" /><path d="m17.5 18 .8.8 1.4-1.6" />
  </svg>
);
const IconTemplate = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3.5" y="5" width="17" height="14" rx="2.5" /><path d="M3.5 10h17" />
  </svg>
);
const IconStamps = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="8" cy="8" r="3.4" /><circle cx="16" cy="16" r="3.4" strokeDasharray="2.4 2.6" /><path d="M14.2 4.9 18.5 9.2M18.5 4.9l-4.3 4.3" />
  </svg>
);
const IconLayout = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3.5" y="3.5" width="7" height="17" rx="1.8" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.8" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.8" />
  </svg>
);
/* Elements: a shape, a letter and a picture corner — the three things the
   panel adds, rather than only one of them. */
const IconShapes = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3.2" y="3.2" width="8.6" height="8.6" rx="1.5" /><circle cx="16.8" cy="16.4" r="4.4" />
    <path d="M14.6 4.4h6M17.6 4.4v6" /><path d="M3.2 20.4l3.4-3.4 2.6 2.6" />
  </svg>
);
const IconBackground = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" /><path d="M10 3.5L3.5 10M20.5 12L12 20.5" />
  </svg>
);

// Six tools, so every one fits a phone's rail without scrolling. Text,
// shapes and images share the Elements panel: they are all "put something
// on the card", and as separate tools the last two fell off the screen.
const RAIL_ITEMS = [
  { id: 'rules', label: 'Rules', Icon: IconRules },
  { id: 'template', label: 'Templates', Icon: IconTemplate },
  { id: 'stamps', label: 'Stamps', Icon: IconStamps },
  { id: 'layout', label: 'Layout', Icon: IconLayout },
  { id: 'elements', label: 'Elements', Icon: IconShapes },
  { id: 'background', label: 'Backdrop', Icon: IconBackground },
];

const PANEL_TITLES = {
  rules: 'Rules',
  template: 'Templates',
  stamps: 'Stamps',
  layout: 'Layout',
  elements: 'Elements',
  background: 'Background',
  'edit-font': 'Font',
  'edit-color': 'Colour',
  'edit-stroke': 'Outline',
};

// Undo/redo over the design object (same coalescing reducer as the
// gift-card designer).
const COALESCE_MS = 350;
const HISTORY_LIMIT = 100;
const historyReducer = (h, action) => {
  switch (action.type) {
    case 'set': {
      const next = typeof action.updater === 'function' ? action.updater(h.present) : action.updater;
      if (next === h.present) return h;
      const coalesce = action.now - h.lastAt < COALESCE_MS;
      const past = coalesce ? h.past : [...h.past, h.present].slice(-HISTORY_LIMIT);
      return { past, present: next, future: [], lastAt: action.now };
    }
    case 'undo':
      if (!h.past.length) return h;
      return { past: h.past.slice(0, -1), present: h.past[h.past.length - 1], future: [h.present, ...h.future], lastAt: 0 };
    case 'redo':
      if (!h.future.length) return h;
      return { past: [...h.past, h.present], present: h.future[0], future: h.future.slice(1), lastAt: 0 };
    default:
      return h;
  }
};

const ink = '#1A1A1E';
const line = '#ECECF0';

const pillStyle = (selected) => ({
  padding: '8px 14px', borderRadius: 100, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  border: selected ? '2px solid #29282D' : `1px solid ${line}`,
  background: selected ? '#29282D' : '#fff',
  color: selected ? '#fff' : '#4B4B52',
});

// Two-up either/or toggle, styled like the item-picker tabs. Tidier than
// wrapping pills for the binary rule choices, and reads clearly as "pick one".
const Segmented = ({ options, value, onChange }) => (
  <div className="v2-cd-seg">
    {options.map((o) => (
      <button
        key={String(o.id)}
        type="button"
        className={`v2-cd-seg-btn ${value === o.id ? 'is-on' : ''}`}
        onClick={() => onChange(o.id)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const fieldLabel = { display: 'block', fontSize: 12.5, fontWeight: 600, color: '#4B4B52', margin: '0 0 6px' };
const fieldInput = { width: '100%', height: 44, padding: '0 14px', boxSizing: 'border-box', border: `1px solid ${line}`, borderRadius: 10, fontSize: 14, fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff' };

const ensureCustom = (c) => (c && c.elements ? c : { ...blankCustom(), ...(c || {}) });

// Pop-out multi-select of the salon's services + products, for the earn /
// redeem item rules. Tab-filtered (Services | Products) with search, so it
// scales to large catalogues.
const ItemPicker = ({ loading, items, selected, onToggle }) => {
  const [tab, setTab] = useState('service');
  const [q, setQ] = useState('');
  if (loading) return <p className="v2-cd-panel-help">Loading your services and products…</p>;
  if (!items.length) return <p className="v2-cd-panel-help">No services or products found yet. Add some to your catalogue and they will appear here.</p>;
  const needle = q.trim().toLowerCase();
  const counts = {
    service: items.filter((i) => i.type === 'service').length,
    product: items.filter((i) => i.type === 'product').length,
  };
  const selCounts = {
    service: items.filter((i) => i.type === 'service' && selected.includes(i.id)).length,
    product: items.filter((i) => i.type === 'product' && selected.includes(i.id)).length,
  };
  const rows = items.filter((it) => it.type === tab && (!needle || it.name.toLowerCase().includes(needle)));
  const tabs = [
    { key: 'service', label: 'Services' },
    { key: 'product', label: 'Products' },
  ];
  return (
    <>
      <div style={{ display: 'flex', gap: 4, background: '#F2F2F4', borderRadius: 10, padding: 4 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            style={{ flex: 1, border: 'none', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', background: tab === t.key ? '#fff' : 'transparent', color: tab === t.key ? '#1A1A1E' : '#757575', boxShadow: tab === t.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}
          >
            {t.label} ({selCounts[t.key]}/{counts[t.key]})
          </button>
        ))}
      </div>
      <div style={{ border: `1px solid ${line}`, borderRadius: 10, overflow: 'hidden', flex: '0 0 auto' }}>
        <input
          type="text"
          value={q}
          onChange={(e) => { const v = e.target.value; setQ(v); }}
          placeholder={`Search ${tab === 'service' ? 'services' : 'products'}`}
          style={{ width: '100%', height: 40, padding: '0 12px', boxSizing: 'border-box', border: 'none', borderBottom: `1px solid ${line}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff' }}
        />
        {rows.length ? rows.map((it, i) => {
          const on = selected.includes(it.id);
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => onToggle(it.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '11px 12px', border: 'none', borderTop: i ? '1px solid #F5F5F7' : 'none', background: on ? 'rgba(0,126,160,0.06)' : '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
            >
              <span style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, border: on ? '2px solid #007EA0' : `1.5px solid ${line}`, background: on ? '#007EA0' : '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {on ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg> : null}
              </span>
              <span style={{ fontSize: 13.5, color: '#1A1A1E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</span>
            </button>
          );
        }) : (
          <p className="v2-cd-panel-help" style={{ padding: '14px 12px', margin: 0 }}>Nothing matches “{q}”.</p>
        )}
      </div>
      <p className="v2-cd-panel-help">{selected.length} selected in total.</p>
    </>
  );
};

const LoyaltyDesigner = ({ scheme, salonName, onClose, onSave, isCreate = false }) => {
  const dispatch = useDispatch();
  // The salon's services + products, for the earn/redeem item pickers.
  const [catalog, setCatalog] = useState({ loading: true, items: [] });
  const [itemPicker, setItemPicker] = useState(null); // null | 'earn' | 'redeem'
  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      dispatch(newServicesAction.listServicesData({})),
      dispatch(productAction.getProductsList({ salonId: localStorage.getItem('salonId') })),
    ]).then(([svcRes, prodRes]) => {
      if (!alive) return;
      const items = [];
      const cats = (svcRes.status === 'fulfilled' && svcRes.value && svcRes.value.listServices) || [];
      cats.forEach((c) => (c.serviceList || []).forEach((s) => {
        if (!s.isDeactivated) items.push({ id: String(s.commonServiceId), type: 'service', name: s.serviceName });
      }));
      const prods = (prodRes.status === 'fulfilled' && prodRes.value && prodRes.value.data
        && prodRes.value.data.responseData && prodRes.value.data.responseData.result) || [];
      prods.forEach((p) => items.push({ id: String(p._id), type: 'product', name: p.productName || p.name || 'Product' }));
      setCatalog({ loading: false, items });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Scheme rules (not part of design undo history) ───
  const [rules, setRules] = useState(() => ({
    name: scheme.name || '',
    mode: scheme.mode,
    earnRule: scheme.earnRule || 'any',
    minSpend: scheme.minSpend || 25,
    earnScope: scheme.earnScope || 'all', // 'all' | 'selected'
    earnItems: scheme.earnItems || [],
    rewardValueMode: scheme.rewardValueMode || 'cap', // 'cap' | 'any'
    rewardCap: scheme.rewardCap || 40,
    dailyLimit: scheme.dailyLimit !== false,
    redeemScope: scheme.redeemScope === 'selected' ? 'selected' : 'all', // 'all' | 'selected'
    redeemItems: scheme.redeemItems || [],
    stampsRequired: scheme.stampsRequired || 6,
    rewardLabel: scheme.rewardLabel || '',
    pointsPerPound: scheme.pointsPerPound || 1,
    pointsTarget: scheme.pointsTarget || 500,
    pointsLabel: scheme.pointsLabel || '',
  }));
  const setRule = (field, value) => setRules((r) => ({ ...r, [field]: value }));
  const toggleRuleItem = (field, id) => setRules((r) => {
    const cur = r[field] || [];
    return { ...r, [field]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  // ─── Design with undo/redo ───
  const [history, dispatchHistory] = useReducer(historyReducer, null, () => ({
    past: [], present: { ...scheme.design }, future: [], lastAt: 0,
  }));
  const draft = history.present;
  const mutate = useCallback((updater) => {
    dispatchHistory({ type: 'set', updater, now: Date.now() });
  }, []);
  const setDesign = useCallback((patch) => mutate((d) => ({ ...d, ...patch })), [mutate]);
  const setCustom = useCallback((patch) => mutate((d) => ({ ...d, custom: { ...ensureCustom(d.custom), ...patch } })), [mutate]);
  const undo = useCallback(() => dispatchHistory({ type: 'undo' }), []);
  const redo = useCallback(() => dispatchHistory({ type: 'redo' }), []);
  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  const custom = ensureCustom(draft.custom);

  const [closing, setClosing] = useState(false);
  const [activeCategory, setActiveCategory] = useState(() => (window.innerWidth < 768 ? null : (isCreate ? 'rules' : 'template')));
  const [activePack, setActivePack] = useState(() => packForIcon(scheme.design?.icon));
  const [bgEditing, setBgEditing] = useState(null); // null | 'solid' | 'from' | 'to'
  const [stampColorEditing, setStampColorEditing] = useState(false);


  // Element selection
  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const blobUrlsRef = useRef([]);
  useEffect(() => () => { blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  // Preview-a-visit. Filling the last slot plays the client's own "Reward
  // unlocked" screen, so the payoff is visible while designing too.
  const [previewFill, setPreviewFill] = useState(null);
  const [popIndex, setPopIndex] = useState(-1);
  const [rewardPreview, setRewardPreview] = useState(false);
  const previewTimer = useRef(null);
  const rewardTimer = useRef(null);
  const isStamps = rules.mode !== 'points';
  const previewVisit = () => {
    if (!isStamps) return;
    const required = rules.stampsRequired || 6;
    const base = Math.ceil(required / 2);
    const next = previewFill == null ? base : (previewFill >= required ? base : previewFill + 1);
    setPreviewFill(next);
    setPopIndex(next - 1);
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => setPopIndex(-1), 650);
    clearTimeout(rewardTimer.current);
    if (next >= required) {
      // Let the final stamp land first, exactly as the client experiences it.
      rewardTimer.current = setTimeout(() => setRewardPreview(true), 700);
    }
  };
  useEffect(() => () => {
    clearTimeout(previewTimer.current);
    clearTimeout(rewardTimer.current);
  }, []);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose(), 180);
  }, [onClose]);

  // A "Selected items" scope with nothing picked is rejected by the API, and
  // it is a rule that could never match anyway. Catch it here so the salon
  // gets told which step is wrong instead of a bare server error.
  const scopeGap = (() => {
    if ((rules.earnScope || 'all') === 'selected' && !(rules.earnItems || []).length) {
      return { message: `Choose at least one item that ${isStamps ? 'earns a stamp' : 'earns points'}, or set earning to Anything.` };
    }
    if ((rules.redeemScope || 'all') === 'selected' && !(rules.redeemItems || []).length) {
      return { message: 'Choose at least one item the reward can be used against, or set redeeming to Anything.' };
    }
    return null;
  })();

  const handleSave = () => {
    if (scopeGap) {
      toast.error(scopeGap.message);
      setActiveCategory('rules');
      return;
    }
    onSave({
      ...scheme,
      ...rules,
      name: rules.name.trim() || (isStamps ? 'Stamp card' : 'Loyalty points'),
      design: draft,
    });
    handleClose();
  };

  // Shared ref-counted scroll lock: overlapping overlays must not restore
  // each other's locked styles.
  useScrollLock(true);

  // Keys
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (e.key === 'Escape') {
        if (contextMenu) return;
        if (selectedId) { setSelectedId(null); setEditingId(null); return; }
        handleClose();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && !typing && !editingId) {
        e.preventDefault();
        removeElement(selectedId);
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || typing || editingId) return;
      const k = e.key.toLowerCase();
      if (k === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if (k === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClose, undo, redo, selectedId, editingId, contextMenu]);

  // ─── Element ops on draft.custom.elements ───
  const updateElement = (id, patch) => mutate((d) => {
    const c = ensureCustom(d.custom);
    return { ...d, custom: { ...c, elements: c.elements.map((el) => (el.id === id ? { ...el, ...patch } : el)) } };
  });
  const updateElements = (patchById) => mutate((d) => {
    const c = ensureCustom(d.custom);
    return { ...d, custom: { ...c, elements: c.elements.map((el) => (patchById[el.id] ? { ...el, ...patchById[el.id] } : el)) } };
  });
  const addElement = (partial) => {
    const id = mintElementId();
    const jitter = () => Math.round((Math.random() - 0.5) * 16);
    mutate((d) => {
      const c = ensureCustom(d.custom);
      return { ...d, custom: { ...c, elements: [...c.elements, { id, x: 50 + jitter(), y: 24 + jitter(), scale: 1, rotation: 0, ...partial }] } };
    });
    return id;
  };
  const removeElement = (id) => {
    mutate((d) => {
      const c = ensureCustom(d.custom);
      return { ...d, custom: { ...c, elements: c.elements.filter((el) => el.id !== id) } };
    });
    setSelectedId((cur) => (cur === id ? null : cur));
    setEditingId((cur) => (cur === id ? null : cur));
  };
  const duplicateElement = (id) => mutate((d) => {
    const c = ensureCustom(d.custom);
    const el = c.elements.find((e) => e.id === id);
    if (!el) return d;
    const copy = { ...el, id: mintElementId(), x: (el.x ?? 50) + 5, y: (el.y ?? 50) + 5 };
    return { ...d, custom: { ...c, elements: [...c.elements, copy] } };
  });
  const reorderElement = (id, dir) => mutate((d) => {
    const c = ensureCustom(d.custom);
    const i = c.elements.findIndex((e) => e.id === id);
    if (i < 0) return d;
    const next = [...c.elements];
    const [el] = next.splice(i, 1);
    if (dir === 'front') next.push(el);
    else if (dir === 'back') next.unshift(el);
    else if (dir === 'forward') next.splice(Math.min(next.length, i + 1), 0, el);
    else next.splice(Math.max(0, i - 1), 0, el);
    return { ...d, custom: { ...c, elements: next } };
  });

  const effInk = useMemo(() => {
    // Mirror of the card's surface logic, for sensible new-element colours
    const c = custom;
    if (c.bgMode === 'solid' || c.bgMode === 'gradient' || c.bgMode === 'photo') return '#F7F5F0';
    return '#F7F5F0';
  }, [custom]);

  const onAddText = (overrides = {}) => {
    const id = addElement({ type: 'text', content: overrides.content || 'Double-click to edit', font: 'fraunces', color: effInk, ...TEXT_DEFAULTS, ...overrides });
    setSelectedId(id);
    if (window.innerWidth < 768) setActiveCategory(null);
  };
  const onAddShape = (shape) => {
    const id = addElement({ type: 'shape', shape, fill: effInk, radius: shape === 'rect' ? 14 : 0, scale: 0.6 });
    setSelectedId(id);
    if (window.innerWidth < 768) setActiveCategory(null);
  };
  const onAddImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    blobUrlsRef.current.push(url);
    const id = addElement({ type: 'image', src: url });
    setSelectedId(id);
    e.target.value = '';
  };
  const onUploadBgPhoto = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    blobUrlsRef.current.push(url);
    setCustom({ bgPhoto: { url, focalX: 50, focalY: 50, zoom: 1 }, bgMode: 'photo' });
    e.target.value = '';
  };

  const selectedEl = useMemo(() => custom.elements.find((el) => el.id === selectedId) || null, [custom.elements, selectedId]);

  useEffect(() => {
    if (!selectedEl && (activeCategory === 'edit-color' || activeCategory === 'edit-stroke' || activeCategory === 'edit-font')) {
      setActiveCategory(null);
    }
  }, [selectedEl, activeCategory]);

  const contextMenuItems = useMemo(() => {
    if (!contextMenu) return [];
    const id = contextMenu.id;
    const idx = custom.elements.findIndex((e) => e.id === id);
    const atFront = idx === custom.elements.length - 1;
    const atBack = idx <= 0;
    return [
      { label: 'Bring to front', onClick: () => reorderElement(id, 'front'), disabled: atFront },
      { label: 'Bring forward', onClick: () => reorderElement(id, 'forward'), disabled: atFront },
      { label: 'Send backward', onClick: () => reorderElement(id, 'backward'), disabled: atBack },
      { label: 'Send to back', onClick: () => reorderElement(id, 'back'), disabled: atBack },
      { divider: true },
      { label: 'Duplicate', onClick: () => duplicateElement(id) },
      { divider: true },
      { label: 'Delete', danger: true, onClick: () => removeElement(id) },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu, custom.elements]);

  const portrait = draft.orientation === 'portrait';
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const cardWidth = portrait ? Math.min(300, vw - 130) : Math.min(440, vw - 72);

  const previewScheme = { ...scheme, ...rules };

  // ─── Panels ───
  const renderPanelBody = () => {
    if (activeCategory === 'rules') {
      // 'any' is the only uncapped option; treat anything else (incl. legacy
      // backend enums and undefined) as capped so the control always resolves.
      const rewardCapped = (rules.rewardValueMode || 'cap') !== 'any';
      return (
        <>
          <div className="v2-cd-step">
            <div className="v2-cd-step-head">
              <span className="v2-cd-step-num">1</span>
              <div>
                <h4 className="v2-cd-step-title">Reward</h4>
                <p className="v2-cd-step-sub">What your clients are working towards.</p>
              </div>
            </div>
            {isStamps ? (
              <>
                <div className="v2-cd-substep">
                  <p className="v2-cd-panel-section-label">Stamps to collect</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[4, 5, 6, 8, 10].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRule('stampsRequired', n)}
                        style={{
                          width: 46, height: 40, borderRadius: 10, fontSize: 14, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                          border: rules.stampsRequired === n ? '2px solid #29282D' : `1px solid ${line}`,
                          background: rules.stampsRequired === n ? '#29282D' : '#fff',
                          color: rules.stampsRequired === n ? '#fff' : '#4B4B52',
                        }}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="v2-cd-substep">
                  <p className="v2-cd-panel-section-label">The reward</p>
                  <input
                    style={fieldInput}
                    type="text"
                    value={rules.rewardLabel}
                    onChange={(e) => { const v = e.target.value; setRule('rewardLabel', v); }}
                    placeholder="e.g. Free blow dry"
                  />
                  <p className="v2-cd-panel-help">The final slot on the card becomes this treat.</p>
                </div>
              </>
            ) : (
              <>
                <div className="v2-cd-substep">
                  <p className="v2-cd-panel-section-label">Earn rate</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={fieldLabel}>Points per £1</label>
                      <NumberField style={fieldInput} min={1} max={20} fallback={1} value={rules.pointsPerPound} onChange={(v) => setRule('pointsPerPound', v)} />
                    </div>
                    <div>
                      <label style={fieldLabel}>Reward at</label>
                      <NumberField style={fieldInput} min={50} step="50" fallback={500} value={rules.pointsTarget} onChange={(v) => setRule('pointsTarget', v)} />
                    </div>
                  </div>
                </div>
                <div className="v2-cd-substep">
                  <p className="v2-cd-panel-section-label">The reward</p>
                  <input
                    style={fieldInput}
                    type="text"
                    value={rules.pointsLabel}
                    onChange={(e) => { const v = e.target.value; setRule('pointsLabel', v); }}
                    placeholder="e.g. Free conditioning treatment"
                  />
                  <p className="v2-cd-panel-help">
                    Clients spend about £{Math.round((rules.pointsTarget || 500) / (rules.pointsPerPound || 1)).toLocaleString()} to earn this. Keep it special but reachable.
                  </p>
                </div>
              </>
            )}
            <div className="v2-cd-substep">
              <p className="v2-cd-panel-section-label">Reward value</p>
              <Segmented
                options={[{ id: 'cap', label: 'Up to a set amount' }, { id: 'any', label: 'Any value' }]}
                value={rewardCapped ? 'cap' : 'any'}
                onChange={(id) => setRule('rewardValueMode', id)}
              />
              {rewardCapped ? (
                <div>
                  <label style={fieldLabel}>Reward covers up to (£)</label>
                  <NumberField
                    style={fieldInput}
                    min={1}
                    fallback={1}
                    value={rules.rewardCap}
                    onChange={(v) => setRule('rewardCap', v)}
                  />
                  <p className="v2-cd-panel-help" style={{ marginTop: 7 }}>
                    The reward is worth up to £{rules.rewardCap}. If the client redeems against a dearer service, they pay the difference. This stops a stack of cheap visits cashing in for a premium treatment.
                  </p>
                </div>
              ) : null}
              {!rewardCapped ? (
                <p className="v2-cd-panel-help" style={{ color: '#A05A1F', fontWeight: 600 }}>
                  No cap: any service can be redeemed free, including your most expensive. Best kept for points schemes, where earning already scales with spend.
                </p>
              ) : null}
            </div>
          </div>

          <div className="v2-cd-step">
            <div className="v2-cd-step-head">
              <span className="v2-cd-step-num">2</span>
              <div>
                <h4 className="v2-cd-step-title">Earning</h4>
                <p className="v2-cd-step-sub">How clients make progress.</p>
              </div>
            </div>
            <div className="v2-cd-substep">
              <p className="v2-cd-panel-section-label">{isStamps ? 'What earns a stamp' : 'What earns points'}</p>
              <Segmented
                options={[{ id: 'all', label: 'Anything' }, { id: 'selected', label: 'Selected items' }]}
                value={rules.earnScope || 'all'}
                onChange={(id) => setRule('earnScope', id)}
              />
              {(rules.earnScope || 'all') === 'selected' ? (
                <button type="button" className="v2-cd-colour-trigger" onClick={() => setItemPicker('earn')}>
                  <span className="v2-cd-colour-trigger-label">Choose services &amp; products</span>
                  <span className="v2-cd-colour-trigger-hex" style={rules.earnItems.length ? undefined : { color: '#d64545' }}>
                    {rules.earnItems.length ? `${rules.earnItems.length} selected` : 'None chosen'}
                  </span>
                </button>
              ) : null}
            </div>
            <div className="v2-cd-substep">
              <p className="v2-cd-panel-section-label">Minimum spend</p>
              <Segmented
                options={[{ id: 'any', label: 'No minimum' }, { id: 'min-spend', label: 'Set a minimum' }]}
                value={rules.earnRule}
                onChange={(id) => setRule('earnRule', id)}
              />
              {rules.earnRule === 'min-spend' ? (
                <div>
                  <label style={fieldLabel}>Minimum spend (£)</label>
                  <NumberField
                    style={fieldInput}
                    min={1}
                    fallback={1}
                    value={rules.minSpend}
                    onChange={(v) => setRule('minSpend', v)}
                  />
                  <p className="v2-cd-panel-help" style={{ marginTop: 7 }}>Visits under £{rules.minSpend} {isStamps ? 'do not earn a stamp' : 'do not earn points'}.</p>
                </div>
              ) : null}
            </div>
            {isStamps ? (
              <div className="v2-cd-substep">
                <p className="v2-cd-panel-section-label">Daily limit</p>
                <Segmented
                  options={[{ id: true, label: 'One stamp per day' }, { id: false, label: 'Every visit counts' }]}
                  value={rules.dailyLimit}
                  onChange={(id) => setRule('dailyLimit', id)}
                />
              </div>
            ) : null}
          </div>

          <div className="v2-cd-step">
            <div className="v2-cd-step-head">
              <span className="v2-cd-step-num">3</span>
              <div>
                <h4 className="v2-cd-step-title">Redeeming</h4>
                <p className="v2-cd-step-sub">Where the reward can be used.</p>
              </div>
            </div>
            <div className="v2-cd-substep">
              <p className="v2-cd-panel-section-label">Redeemable against</p>
              <Segmented
                options={[{ id: 'all', label: 'Anything' }, { id: 'selected', label: 'Selected items' }]}
                value={rules.redeemScope || 'all'}
                onChange={(id) => setRule('redeemScope', id)}
              />
              {(rules.redeemScope || 'all') === 'selected' ? (
                <button type="button" className="v2-cd-colour-trigger" onClick={() => setItemPicker('redeem')}>
                  <span className="v2-cd-colour-trigger-label">Choose services &amp; products</span>
                  <span className="v2-cd-colour-trigger-hex" style={rules.redeemItems.length ? undefined : { color: '#d64545' }}>
                    {rules.redeemItems.length ? `${rules.redeemItems.length} selected` : 'None chosen'}
                  </span>
                </button>
              ) : null}
            </div>
          </div>

          <p className="v2-cd-panel-help" style={{ padding: '0 2px' }}>
            Always on: clients earn on completed, paid visits only. Tips, deposits and gift card purchases never earn.
          </p>
        </>
      );
    }
    if (activeCategory === 'template') {
      return (
        <>
          <p className="v2-cd-panel-help">Designed starting points, shown as your actual card. Your stamp count and orientation stay as they are.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
            {CARD_TEMPLATES.map((t) => {
              const selected = ['themeId', 'icon', 'stampShape', 'header', 'fillStyle']
                .every((k) => (draft[k] || '') === (t.design[k] || ''));
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    const m = materialiseTemplate(t.design, salonName);
                    setDesign({ ...m, custom: m.custom ? { ...blankCustom(), ...m.custom } : { ...ensureCustom(draft.custom), bgMode: null, elements: [] } });
                    // On mobile the panel is a sheet sitting over the card, so
                    // picking a template left the result hidden behind it —
                    // the tap needs to double as confirmation and dismissal,
                    // same as the other add-content panels already do.
                    if (window.innerWidth < 768) setActiveCategory(null);
                  }}
                  style={{
                    border: selected ? '2px solid #007EA0' : `1px solid ${line}`,
                    boxShadow: selected ? '0 0 0 3px rgba(0,126,160,0.15)' : '0 1px 4px rgba(26,26,30,0.05)',
                    borderRadius: 14, padding: '12px 12px 9px', cursor: 'pointer', background: '#fff', fontFamily: 'inherit',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7,
                  }}
                >
                  <span style={{ pointerEvents: 'none', display: 'block' }}>
                    <LoyaltyCard
                      design={{ ...materialiseTemplate(t.design, salonName), custom: t.design.custom ? materialiseTemplate(t.design, salonName).custom : null, orientation: 'landscape', layout: 'grid' }}
                      scheme={previewScheme}
                      salonName={salonName}
                      width={270}
                    />
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: selected ? '#007EA0' : ink }}>{t.name}</span>
                </button>
              );
            })}
          </div>
        </>
      );
    }
    if (activeCategory === 'stamps') {
      const pack = ICON_PACKS.find((p) => p.id === activePack) || ICON_PACKS[0];
      const themeAccent = (THEMES.find((t) => t.id === draft.themeId) || THEMES[0]).accent;
      return (
        <>
          <div style={{ display: 'flex', gap: 6 }}>
            {ICON_PACKS.map((p) => (
              <button key={p.id} type="button" onClick={() => setActivePack(p.id)} style={pillStyle(activePack === p.id)}>
                {p.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(48px, 1fr))', gap: 8 }}>
            {pack.icons.map((ic) => {
              const selected = draft.icon === ic.id;
              return (
                <button
                  key={ic.id}
                  type="button"
                  title={ic.label}
                  aria-label={ic.label}
                  onClick={() => setDesign({ icon: ic.id })}
                  style={{
                    aspectRatio: '1', borderRadius: 10, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    border: selected ? '2px solid #29282D' : `1px solid ${line}`,
                    background: selected && !ic.emoji ? '#29282D' : '#fff',
                    color: selected && !ic.emoji ? '#fff' : '#4B4B52',
                    boxShadow: selected && ic.emoji ? '0 0 0 3px rgba(41,40,45,0.14)' : 'none',
                  }}
                >
                  {ic.emoji ? (
                    <span style={{ fontSize: 21, lineHeight: 1 }} aria-hidden>{ic.emoji}</span>
                  ) : (
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{ic.svg}</svg>
                  )}
                </button>
              );
            })}
          </div>
          <p className="v2-cd-panel-section-label">Stamp colour</p>
          <button type="button" className="v2-cd-colour-trigger" onClick={() => setStampColorEditing(true)}>
            <span className="v2-cd-colour-trigger-swatch" style={{ background: draft.stampColor || themeAccent }} />
            <span className="v2-cd-colour-trigger-label">{isStamps ? 'Stamp colour' : 'Points colour'}</span>
            <span className="v2-cd-colour-trigger-hex">{draft.stampColor ? draft.stampColor.toUpperCase() : 'Theme'}</span>
          </button>

          {isStamps && (
            <>
              <p className="v2-cd-panel-section-label">Stamp shape</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {STAMP_SHAPES.map((sh) => (
                  <button key={sh.id} type="button" onClick={() => setDesign({ stampShape: sh.id })} style={pillStyle((draft.stampShape || 'circle') === sh.id)}>
                    {sh.label}
                  </button>
                ))}
              </div>
            </>
          )}
          {isStamps && !portrait && (
            <>
              <p className="v2-cd-panel-section-label">Stamp position</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {STAMP_BLOCKS.map((b) => (
                  <button key={b.id} type="button" onClick={() => setDesign({ stampBlock: b.id })} style={pillStyle((draft.stampBlock || 'full') === b.id)}>
                    {b.label}
                  </button>
                ))}
              </div>
              <p className="v2-cd-panel-help">Half-width stamps leave room for an illustration or your own words on the other side.</p>
            </>
          )}
          {isStamps && (
            <>
              <p className="v2-cd-panel-section-label">Empty slots show</p>
              <div style={{ display: 'flex', gap: 8 }}>
                {SLOT_LABELS.map((sl) => (
                  <button key={sl.id} type="button" onClick={() => setDesign({ slotLabel: sl.id })} style={pillStyle((draft.slotLabel || 'icon') === sl.id)}>
                    {sl.label}
                  </button>
                ))}
              </div>
            </>
          )}
          {isStamps && !portrait && (
            <>
              <p className="v2-cd-panel-section-label">Stamp arrangement</p>
              <div style={{ display: 'flex', gap: 8 }}>
                {STAMP_LAYOUTS.map((l) => (
                  <button key={l.id} type="button" onClick={() => setDesign({ layout: l.id })} style={pillStyle(draft.layout === l.id)}>
                    {l.label}
                  </button>
                ))}
              </div>
            </>
          )}
          {isStamps && (
            <>
              <p className="v2-cd-panel-section-label">Stamp style</p>
              <div style={{ display: 'flex', gap: 8 }}>
                {FILL_STYLES.map((f) => (
                  <button key={f.id} type="button" onClick={() => setDesign({ fillStyle: f.id })} style={pillStyle(draft.fillStyle === f.id)}>
                    {f.label}
                  </button>
                ))}
              </div>
              <p className="v2-cd-panel-help">Pressed ink lands each stamp slightly askew, like a real hand stamp.</p>
            </>
          )}
          <p className="v2-cd-panel-help">The final slot always shows the reward.</p>
        </>
      );
    }
    if (activeCategory === 'layout') {
      return (
        <>
          <p className="v2-cd-panel-section-label">Card orientation</p>
          <div style={{ display: 'flex', gap: 8 }}>
            {ORIENTATIONS.map((o) => {
              const selected = (draft.orientation || 'landscape') === o.id;
              return (
                <button key={o.id} type="button" onClick={() => setDesign({ orientation: o.id })} style={{ ...pillStyle(selected), display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                  <span style={{
                    width: o.id === 'portrait' ? 9 : 15, height: o.id === 'portrait' ? 13 : 10,
                    borderRadius: 2.5, border: '1.6px solid currentColor', display: 'inline-block',
                  }} />
                  {o.label}
                </button>
              );
            })}
          </div>
          <p className="v2-cd-panel-section-label">Header</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {HEADER_STYLES.map((h) => (
              <button
                key={h.id}
                type="button"
                onClick={() => mutate((d) => {
                  const c = ensureCustom(d.custom);
                  if (h.id === 'none' && !c.elements.some((el) => el.type === 'text')) {
                    return { ...d, header: 'none', custom: { ...c, elements: [...c.elements, ...headerToElements(salonName, isStamps, rules)] } };
                  }
                  return { ...d, header: h.id };
                })}
                style={pillStyle((draft.header || 'classic') === h.id)}
              >
                {h.label}
              </button>
            ))}
          </div>
          {(draft.header || 'classic') === 'none' ? (
            <p className="v2-cd-panel-help">The card text is now yours: click any word on the card to restyle, move or delete it.</p>
          ) : null}
          <p className="v2-cd-panel-section-label">Progress footer</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[{ id: 'standard', label: 'Shown' }, { id: 'hidden', label: 'Hidden' }, { id: 'custom', label: 'Custom text' }].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => mutate((d) => {
                  const c = ensureCustom(d.custom);
                  if (f.id === 'custom' && !c.elements.some((el) => el.id === 'fx-reward')) {
                    const rewardText = isStamps
                      ? `${rules.rewardLabel || 'Free treatment'} at ${rules.stampsRequired || 6}`
                      : `${rules.pointsLabel || 'Free treatment'} at ${(rules.pointsTarget || 500).toLocaleString()}`;
                    return { ...d, footer: 'custom', custom: { ...c, elements: [...c.elements, { id: 'fx-reward', type: 'text', content: rewardText, font: 'inter', fontSize: 16, bold: true, color: '', x: 50, y: 90, scale: 1, rotation: 0 }] } };
                  }
                  return { ...d, footer: f.id };
                })}
                style={pillStyle((draft.footer || 'standard') === f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          {(draft.footer || 'standard') === 'custom' ? (
            <p className="v2-cd-panel-help">The reward line is now a text element on the card: drag, restyle or delete it like any other. The stamps still show progress on their own.</p>
          ) : null}
        </>
      );
    }
    /* Text, shapes and images are all "put something on the card", so they
       share one panel. Eight separate tools overflowed the phone's rail and
       the last two were never discovered. */
    if (activeCategory === 'elements') {
      return (
        <>
          <p className="v2-cd-panel-section-label">Text</p>
          <button type="button" className="v2-cd-panel-cta" onClick={() => onAddText({ content: 'Your words' })}>Add a text box</button>
          <div className="v2-cd-panel-presets">
            {TEXT_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className="v2-cd-panel-preset"
                style={{ fontWeight: p.bold ? 700 : 500, fontStyle: p.italic ? 'italic' : 'normal', fontSize: Math.min(22, p.fontSize) }}
                onClick={() => onAddText({ content: p.label, bold: p.bold, italic: p.italic, fontSize: p.fontSize })}
              >
                {p.label}
              </button>
            ))}
          </div>

          <p className="v2-cd-panel-section-label">Shapes</p>
          <div className="v2-cd-panel-shapes">
            {SHAPE_TYPES.map((sh) => (
              <button key={sh.id} type="button" className="v2-cd-panel-shape" aria-label={sh.label} onClick={() => onAddShape(sh.id)}>
                <ShapeRenderer shape={sh.id} fill="#4B4B52" radius={sh.id === 'rect' ? 14 : 0} />
              </button>
            ))}
          </div>

          <p className="v2-cd-panel-section-label">Your own artwork</p>
          <label className="v2-cd-panel-cta" style={{ cursor: 'pointer' }}>
            Upload an image
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onAddImage} />
          </label>

          <p className="v2-cd-panel-help">Drag to place, corners to resize, double-click text to edit. Emoji work too: ✦ ✶ 🌸. Right-click an element for layering.</p>
        </>
      );
    }
    if (activeCategory === 'background') {
      const mode = custom.bgMode;
      return (
        <>
          <p className="v2-cd-panel-section-label">Theme palettes</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(36px, 1fr))', gap: 8 }}>
            {THEMES.map((t) => {
              const selected = !mode && draft.themeId === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  title={t.name}
                  aria-label={t.name}
                  onClick={() => mutate((d) => ({ ...d, themeId: t.id, custom: { ...ensureCustom(d.custom), bgMode: null } }))}
                  style={{
                    width: '100%', aspectRatio: '1', borderRadius: '50%', cursor: 'pointer', background: t.bg,
                    border: selected ? '2px solid #007EA0' : '1px solid rgba(0,0,0,0.12)',
                    boxShadow: selected ? '0 0 0 3px rgba(0,126,160,0.18)' : 'none',
                  }}
                />
              );
            })}
          </div>
          <p className="v2-cd-panel-section-label">Custom background</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[{ id: 'solid', label: 'Solid' }, { id: 'gradient', label: 'Gradient' }, { id: 'photo', label: 'Photo' }, { id: 'pattern', label: 'Pattern' }].map((m) => (
              <button key={m.id} type="button" style={pillStyle(mode === m.id)} onClick={() => setCustom({ bgMode: m.id })}>
                {m.label}
              </button>
            ))}
          </div>
          {mode === 'solid' && (
            <button type="button" className="v2-cd-colour-trigger" onClick={() => setBgEditing('solid')}>
              <span className="v2-cd-colour-trigger-swatch" style={{ background: custom.bgColor }} />
              <span className="v2-cd-colour-trigger-label">Colour</span>
              <span className="v2-cd-colour-trigger-hex">{(custom.bgColor || '').toUpperCase()}</span>
            </button>
          )}
          {mode === 'gradient' && (
            <>
              {['from', 'to'].map((k) => (
                <button key={k} type="button" className="v2-cd-colour-trigger" onClick={() => setBgEditing(k)}>
                  <span className="v2-cd-colour-trigger-swatch" style={{ background: (custom.bgGradient || {})[k] }} />
                  <span className="v2-cd-colour-trigger-label">{k === 'from' ? 'Start' : 'End'}</span>
                  <span className="v2-cd-colour-trigger-hex">{((custom.bgGradient || {})[k] || '').toUpperCase()}</span>
                </button>
              ))}
              <div>
                <p className="v2-cd-panel-section-label">Angle</p>
                <input
                  type="range"
                  min="0"
                  max="360"
                  step="15"
                  value={(custom.bgGradient || {}).angle ?? 135}
                  onChange={(e) => { const v = parseInt(e.target.value, 10); setCustom({ bgGradient: { ...custom.bgGradient, angle: v } }); }}
                  style={{ width: '100%' }}
                />
              </div>
            </>
          )}
          {mode === 'photo' && (
            <>
              {custom.bgPhoto && custom.bgPhoto.url ? (
                <img src={custom.bgPhoto.url} alt="" style={{ width: '100%', borderRadius: 10, display: 'block' }} />
              ) : null}
              <label className="v2-cd-panel-cta" style={{ cursor: 'pointer' }}>
                {custom.bgPhoto ? 'Replace photo' : 'Upload a photo'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onUploadBgPhoto} />
              </label>
            </>
          )}
          {mode === 'pattern' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {OCCASIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setCustom({ bgPattern: { occasion: o.id }, bgMode: 'pattern' })}
                  style={{
                    border: (custom.bgPattern || {}).occasion === o.id ? '2px solid #007EA0' : `1px solid ${line}`,
                    borderRadius: 10, overflow: 'hidden', cursor: 'pointer', padding: 0, background: '#fff',
                  }}
                >
                  <span style={{ display: 'block', height: 52, pointerEvents: 'none' }}><AbstractScene occasion={o.id} /></span>
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: ink, padding: '5px 8px', textAlign: 'left' }}>{o.name}</span>
                </button>
              ))}
            </div>
          )}
          {mode ? (
            <button type="button" className="sp-btn-secondary" style={{ alignSelf: 'flex-start', flex: '0 0 auto', padding: '6px 14px' }} onClick={() => setCustom({ bgMode: null })}>Use theme colour</button>
          ) : null}
        </>
      );
    }
    if (activeCategory === 'edit-font') {
      if (!selectedEl) return <p className="v2-cd-panel-help">Select an element first.</p>;
      return (
        <>
          {selectedEl.type === 'text' ? (
            <>
              <p className="v2-cd-panel-section-label">Effects</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={pillStyle(!!selectedEl.shadow)}
                  onClick={() => updateElement(selectedEl.id, { shadow: !selectedEl.shadow })}
                >
                  Drop shadow
                </button>
                <button
                  type="button"
                  style={pillStyle(selectedEl.badge === 'pill')}
                  onClick={() => updateElement(selectedEl.id, { badge: selectedEl.badge === 'pill' ? null : 'pill' })}
                >
                  Pill badge
                </button>
              </div>
              <p className="v2-cd-panel-section-label">Font</p>
            </>
          ) : null}
          {FONT_OPTIONS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`v2-cd-font-tile ${(selectedEl.font || 'fraunces') === f.id ? 'is-active' : ''}`}
              style={{ fontFamily: f.family }}
              onClick={() => updateElement(selectedEl.id, { font: f.id })}
            >
              {f.label}
            </button>
          ))}
        </>
      );
    }
    if (activeCategory === 'edit-color' || activeCategory === 'edit-stroke') {
      if (!selectedEl) return <p className="v2-cd-panel-help">Select an element first.</p>;
      const field = activeCategory === 'edit-stroke' ? 'stroke' : (selectedEl.type === 'shape' ? 'fill' : 'color');
      return (
        <ColourPicker
          value={selectedEl[field] || '#FFFFFF'}
          inline
          paletteAa={false}
          onChange={(hex) => updateElement(selectedEl.id, { [field]: hex })}
        />
      );
    }
    return null;
  };

  // Pop-out sub-screens render as a single overlay inside the panel (it
  // covers the panel but not the rail, and unmounts with the panel). Each
  // is scoped to its own panel so switching tools never shows a stray one.
  const resetSubscreens = () => { setItemPicker(null); setStampColorEditing(false); setBgEditing(null); };
  const activeSubsheet = (() => {
    if (activeCategory === 'rules' && itemPicker) {
      const field = itemPicker === 'earn' ? 'earnItems' : 'redeemItems';
      return {
        title: itemPicker === 'earn' ? 'Which items earn' : 'What the reward applies to',
        onBack: () => setItemPicker(null),
        content: (
          <ItemPicker
            loading={catalog.loading}
            items={catalog.items}
            selected={rules[field]}
            onToggle={(id) => toggleRuleItem(field, id)}
          />
        ),
      };
    }
    if (activeCategory === 'stamps' && stampColorEditing) {
      const themeAccent = (THEMES.find((t) => t.id === draft.themeId) || THEMES[0]).accent;
      return {
        title: isStamps ? 'Stamp colour' : 'Points colour',
        onBack: () => setStampColorEditing(false),
        content: (
          <>
            <ColourPicker
              value={draft.stampColor || themeAccent}
              inline
              paletteAa={false}
              onChange={(hex) => setDesign({ stampColor: hex })}
            />
            {draft.stampColor ? (
              <button type="button" className="sp-btn-secondary" style={{ alignSelf: 'flex-start', flex: '0 0 auto', padding: '6px 14px' }} onClick={() => { setDesign({ stampColor: null }); setStampColorEditing(false); }}>Back to theme colour</button>
            ) : null}
          </>
        ),
      };
    }
    if (activeCategory === 'background' && bgEditing) {
      const value = bgEditing === 'solid' ? custom.bgColor : (custom.bgGradient || {})[bgEditing] || '#29282D';
      const title = bgEditing === 'solid' ? 'Background colour' : (bgEditing === 'from' ? 'Gradient start' : 'Gradient end');
      return {
        title,
        onBack: () => setBgEditing(null),
        content: (
          <ColourPicker
            value={value}
            inline
            paletteAa={false}
            onChange={(hex) => {
              if (bgEditing === 'solid') setCustom({ bgColor: hex, bgMode: 'solid' });
              else setCustom({ bgGradient: { ...custom.bgGradient, [bgEditing]: hex }, bgMode: 'gradient' });
            }}
          />
        ),
      };
    }
    return null;
  })();

  const modal = (
    <>
      <div className={`v2-cd-backdrop ${closing ? 'is-closing' : ''}`} onClick={handleClose} />
      <div
        className={['voucher-v2', 'v2-cd-modal', closing ? 'is-closing' : '', activeCategory ? 'has-panel' : ''].join(' ').trim()}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Loyalty card designer"
      >
        <header className="v2-cd-topbar">
          <button type="button" className="v2-cd-iconbtn" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </button>
          <div className="v2-cd-history">
            <button type="button" className="v2-cd-iconbtn" onClick={undo} disabled={!canUndo} aria-label="Undo" title="Undo (⌘Z)"><UndoIcon /></button>
            <button type="button" className="v2-cd-iconbtn" onClick={redo} disabled={!canRedo} aria-label="Redo" title="Redo (⇧⌘Z)"><RedoIcon /></button>
          </div>
          <div className="v2-cd-topbar-name">
            <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.005em', color: ink, padding: '6px 10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {rules.name.trim() || 'Loyalty card'}
            </span>
          </div>
          <div className="v2-cd-topbar-actions">
            <button type="button" className="sp-btn-secondary" onClick={handleClose}>Cancel</button>
            <button type="button" className="sp-btn-primary" onClick={handleSave}>{isCreate ? 'Create scheme' : 'Save changes'}</button>
          </div>
        </header>

        <div className="v2-cd-ctxbar-host">
          <DesignerTopContextBar
            element={selectedEl}
            multiCount={selectedEl ? 1 : 0}
            isGroup={false}
            onChange={(patch) => selectedEl && updateElement(selectedEl.id, patch)}
            onOpenFontPanel={() => setActiveCategory('edit-font')}
            onOpenColorPanel={() => setActiveCategory('edit-color')}
            onOpenStrokePanel={() => setActiveCategory('edit-stroke')}
          />
        </div>

        <nav className="v2-cd-rail" aria-label="Design tools">
          {RAIL_ITEMS.map((item) => {
            const isActive = activeCategory === item.id;
            const Icon = item.Icon;
            return (
              <button
                key={item.id}
                type="button"
                className={`v2-cd-rail-btn ${isActive ? 'is-active' : ''}`}
                onClick={() => { resetSubscreens(); setActiveCategory(isActive ? null : item.id); }}
                aria-pressed={isActive}
                aria-label={item.label}
              >
                <span className="v2-cd-rail-icon"><Icon /></span>
                <span className="v2-cd-rail-label">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {activeCategory && (
          <aside className="v2-cd-panel">
            <div className="v2-cd-panel-header">
              <h3 className="v2-cd-panel-title">{PANEL_TITLES[activeCategory] || ''}</h3>
              <button type="button" className="v2-cd-iconbtn" onClick={() => { resetSubscreens(); setActiveCategory(null); }} aria-label="Close panel"><CloseIcon /></button>
            </div>
            <div className="v2-cd-panel-body">{renderPanelBody()}</div>
            {activeSubsheet && (
              <section className="v2-cd-subsheet" aria-label={activeSubsheet.title}>
                <div className="v2-cd-subsheet-head">
                  <button type="button" className="v2-cd-iconbtn" onClick={activeSubsheet.onBack} aria-label="Back"><BackChevron /></button>
                  <h3 className="v2-cd-panel-title">{activeSubsheet.title}</h3>
                </div>
                <div className="v2-cd-subsheet-body">{activeSubsheet.content}</div>
              </section>
            )}
          </aside>
        )}

        <main className="v2-cd-canvas-area" onClick={() => { resetSubscreens(); setActiveCategory(null); setSelectedId(null); }}>
          <div className="v2-cd-canvas-wrap" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
            <LoyaltyCard
              design={draft}
              scheme={previewScheme}
              state={{ filled: previewFill != null ? previewFill : undefined }}
              salonName={salonName}
              width={cardWidth}
              animateIndex={popIndex}
              editable
              selectedId={selectedId}
              selectedIds={selectedId ? [selectedId] : []}
              editingId={editingId}
              onElementSelect={(id) => { setSelectedId(id); if (id !== editingId) setEditingId(null); }}
              onElementChange={updateElement}
              onElementsChange={updateElements}
              onElementDuplicate={duplicateElement}
              onElementRemove={removeElement}
              onElementDoubleClick={(id) => { setSelectedId(id); setEditingId(id); }}
              onElementContextMenu={(id, x, y) => { setSelectedId(id); setContextMenu({ id, x, y }); }}
              onElementCommitEdit={() => setEditingId(null)}
              onConvertHeader={(which) => {
                const centre = (draft.header || 'classic') === 'centre';
                const seeds = centre
                  ? headerToElements(salonName, isStamps, rules)
                  : [
                    { id: 'hx-title', type: 'text', content: salonName || 'Your Salon', font: 'fraunces', fontSize: 34, bold: true, color: '', x: 26, y: 13, scale: 1, rotation: 0 },
                    { id: 'hx-tag', type: 'text', content: 'LOYALTY', font: 'inter', fontSize: 14, bold: true, color: '', x: 86, y: 13, scale: 1, rotation: 0 },
                  ];
                mutate((d) => {
                  const cu = ensureCustom(d.custom);
                  const fresh = seeds.filter((el) => !cu.elements.some((e) => e.id === el.id));
                  return { ...d, header: 'none', custom: { ...cu, elements: [...cu.elements, ...fresh] } };
                });
                const target = which === 'tag' ? 'hx-tag' : which === 'tagline' ? 'hx-tagline' : 'hx-title';
                setSelectedId(target);
              }}
            />
            {isStamps && (
              <button
                type="button"
                onClick={previewVisit}
                style={{ padding: '9px 16px', borderRadius: 100, border: '1px solid rgba(0,126,160,0.4)', background: '#fff', fontSize: 12.5, fontWeight: 700, color: '#007EA0', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Preview a visit
              </button>
            )}
          </div>
        </main>

        {contextMenu && (
          <DesignerContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={contextMenuItems}
            onClose={() => setContextMenu(null)}
          />
        )}

        {/* The payoff of Preview a visit: the exact screen the client gets
            when their last stamp lands, from the design being edited. */}
        {rewardPreview && (
          <RewardUnlockedView
            zIndex={100020} /* the designer itself sits at 99999 */
            design={draft}
            scheme={previewScheme}
            state={{ filled: rules.stampsRequired || 6, rewardsAvailable: 1 }}
            salonName={salonName}
            rewardName={rules.rewardLabel || 'your reward'}
            subline={`Your card is full at ${salonName}. Show this on your next visit to claim it.`}
            caption="This is what your client sees the moment their card fills."
            actions={(
              <button
                type="button"
                className="reward-full-btn"
                onClick={() => { setRewardPreview(false); setPreviewFill(null); setPopIndex(-1); }}
              >
                Close preview
              </button>
            )}
          />
        )}
      </div>
    </>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default LoyaltyDesigner;
